import { createContext, useContext, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PAYNOW_TIMER } from "../../constants/paynow.c";
import { useCountdown } from "./hooks/useCountdown";
import { usePayNowStatusPolling } from "./hooks/usePayNowStatusPolling";
import "./style.css";

type PayNowQrProps = {
  qrImage: string;
  amount?: string | number | null;
  currency?: string;
  merchantName?: string;
  address?: string;
  /** Transaction UUID to display and poll status for */
  transactionId?: string;
  onBack?: () => void;
  navigate?: (path: string) => void;
  /** Seconds allowed to scan. Defaults to 5 minutes. */
  durationSeconds?: number;
  children: ReactNode;
};

type PayNowQrContextValue = Omit<PayNowQrProps, "children"> & {
  durationSeconds: number;
  secondsLeft: number;
  expired: boolean;
  progress: number;
  displayAmount: string | null;
};

const PayNowQrContext = createContext<PayNowQrContextValue | null>(null);

const usePayNowQrContext = () => {
  const context = useContext(PayNowQrContext);
  if (!context) {
    throw new Error("PayNowQr.* components must be rendered inside <PayNowQr>");
  }
  return context;
};

const formatAmount = (amount?: string | number | null, currency?: string) => {
  if (amount === undefined || amount === null || amount === "") {
    return null;
  }
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) {
    return `${currency || ""} ${amount}`.trim();
  }
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(numeric);
  return `${currency || ""} ${formatted}`.trim();
};

const formatTimer = (totalSeconds: number) => {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Compound root: owns the countdown + status polling and renders the card
 * chrome. Compose the panel from the PayNowQr.* subcomponents, or use the
 * default PayNowQrDisplay arrangement below.
 */
const PayNowQr = ({
  children,
  durationSeconds = PAYNOW_TIMER.SCAN_SECONDS,
  ...props
}: PayNowQrProps) => {
  const { qrImage, amount, currency, transactionId, navigate } = props;
  const { secondsLeft, expired, progress } = useCountdown(
    durationSeconds,
    qrImage
  );
  usePayNowStatusPolling({
    transactionId,
    navigate,
    expired,
    resetKey: qrImage,
  });

  const value: PayNowQrContextValue = {
    ...props,
    durationSeconds,
    secondsLeft,
    expired,
    progress,
    displayAmount: formatAmount(amount, currency),
  };

  return (
    <PayNowQrContext.Provider value={value}>
      <div className="paynow-qr-panel">
        <div className={`paynow-qr-card${expired ? " is-expired" : ""}`}>
          {children}
        </div>
      </div>
    </PayNowQrContext.Provider>
  );
};

const Header = () => {
  const { t } = useTranslation();
  const { merchantName, address } = usePayNowQrContext();
  return (
    <div className="paynow-qr-card-header">
      <p className="paynow-qr-eyebrow">{t("merchant.PayNow_Title")}</p>
      {merchantName ? (
        <h2 className="paynow-qr-merchant" title={merchantName}>
          {merchantName}
        </h2>
      ) : null}
      {address ? (
        <p className="paynow-qr-address" title={address}>
          {address}
        </p>
      ) : null}
    </div>
  );
};

const Amount = () => {
  const { t } = useTranslation();
  const { displayAmount } = usePayNowQrContext();
  if (!displayAmount) return null;
  return (
    <div className="paynow-qr-amount-block">
      <span className="paynow-qr-amount-label">
        {t("merchant.Payment_Information.fields.Amount")}
      </span>
      <span className="paynow-qr-amount-value">{displayAmount}</span>
    </div>
  );
};

const Timer = () => {
  const { t } = useTranslation();
  const { expired, secondsLeft, durationSeconds, progress } =
    usePayNowQrContext();
  return (
    <div className="paynow-qr-timer">
      <div className="paynow-qr-timer-row">
        <span className="paynow-qr-timer-label">
          {expired
            ? t("merchant.PayNow_Timer_Expired")
            : t("merchant.PayNow_Timer_Label")}
        </span>
        <span
          className={`paynow-qr-timer-value${
            secondsLeft <= PAYNOW_TIMER.URGENT_THRESHOLD_SECONDS
              ? " is-urgent"
              : ""
          }`}
        >
          {formatTimer(secondsLeft)}
        </span>
      </div>
      <div
        className="paynow-qr-timer-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={durationSeconds}
        aria-valuenow={secondsLeft}
      >
        <span style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

const Frame = () => {
  const { t } = useTranslation();
  const { qrImage, expired } = usePayNowQrContext();
  return (
    <div className={`paynow-qr-frame${expired ? " is-expired" : ""}`}>
      <img src={qrImage} alt="PayNow QR" />
      {expired ? (
        <div className="paynow-qr-expired-overlay">
          <span>{t("merchant.PayNow_Timer_Expired")}</span>
        </div>
      ) : null}
    </div>
  );
};

const Hint = () => {
  const { t } = useTranslation();
  const { expired } = usePayNowQrContext();
  return (
    <p className="paynow-qr-hint">
      {expired
        ? t("merchant.PayNow_Timer_Expired_Hint")
        : t("merchant.PayNow_Scan_Hint")}
    </p>
  );
};

const Meta = () => {
  const { t } = useTranslation();
  const { transactionId } = usePayNowQrContext();
  if (!transactionId) return null;
  return (
    <div className="paynow-qr-meta">
      <span className="paynow-qr-meta-label">
        {t("merchant.PayNow_Transaction_Id")}
      </span>
      <span className="paynow-qr-meta-value">{transactionId}</span>
    </div>
  );
};

const BackButton = () => {
  const { t } = useTranslation();
  const { onBack, expired } = usePayNowQrContext();
  if (!onBack) return null;
  return (
    <button type="button" className="paynow-qr-back-btn" onClick={onBack}>
      {expired
        ? t("merchant.button.GeneratePayNowQR")
        : t("merchant.button.cancel")}
    </button>
  );
};

PayNowQr.Header = Header;
PayNowQr.Amount = Amount;
PayNowQr.Timer = Timer;
PayNowQr.Frame = Frame;
PayNowQr.Hint = Hint;
PayNowQr.Meta = Meta;
PayNowQr.BackButton = BackButton;

export { PayNowQr };

type PayNowQrDisplayProps = Omit<PayNowQrProps, "children">;

/** Default arrangement of the PayNowQr compound components. */
const PayNowQrDisplay = (props: PayNowQrDisplayProps) => (
  <PayNowQr {...props}>
    <PayNowQr.Header />
    <PayNowQr.Amount />
    <PayNowQr.Timer />
    <PayNowQr.Frame />
    <PayNowQr.Hint />
    <PayNowQr.Meta />
    <PayNowQr.BackButton />
  </PayNowQr>
);

export default PayNowQrDisplay;
